/**
 * Ports — the interfaces that keep `core` free of any platform.
 *
 * Every capability core needs from the outside world is declared here as an
 * interface. Adapters implement them: over HTTP in dev, over the GitHub API in
 * production, over `node:fs` inside GitHub Actions. Core never imports one.
 *
 * This is the seam that makes a future Tauri/Expo/server shell additive rather
 * than a rewrite.
 */

/** One file in the vault. `sha` is an opaque content token used for conflict detection. */
export interface VaultEntry {
  path: string;
  sha: string;
  size?: number;
}

export interface VaultFile {
  path: string;
  content: string;
  sha: string;
}

export interface VaultFS {
  /** Every file in the vault, in one call. Adapters should make this cheap. */
  list(): Promise<VaultEntry[]>;
  read(path: string): Promise<VaultFile>;
  /**
   * Write a file. When `baseSha` is supplied and no longer matches the stored
   * content, implementations MUST throw {@link VaultConflictError} rather than
   * clobbering — the caller replays its semantic mutation against fresh content.
   */
  write(path: string, content: string, baseSha?: string): Promise<{ sha: string }>;
  remove(path: string, baseSha?: string): Promise<void>;
}

/** Thrown when a write loses a race. Callers re-read, re-apply intent, and retry. */
export class VaultConflictError extends Error {
  override readonly name = 'VaultConflictError';
  constructor(
    readonly path: string,
    readonly expectedSha: string | undefined,
    readonly actualSha: string | undefined,
  ) {
    super(`Conflict writing ${path}: expected sha ${expectedSha ?? '(none)'}, found ${actualSha ?? '(none)'}`);
  }
}

export class VaultNotFoundError extends Error {
  override readonly name = 'VaultNotFoundError';
  constructor(readonly path: string) {
    super(`Not found in vault: ${path}`);
  }
}

/** Injected so time-dependent widgets and history math are testable. */
export interface Clock {
  now(): Date;
}

export const systemClock: Clock = {
  now: () => new Date(),
};

/** A fixed clock for tests. */
export function fixedClock(iso: string): Clock {
  const at = new Date(iso);
  return { now: () => new Date(at) };
}
